import { useState, useEffect, useCallback, useRef } from "react";
import { LAYERED_EDIT_WARNING, type LayeredRenderResult } from "@shared/layered-edit";
import { safeTextEditDiagnostic, type TextEditDiagnostic } from "@shared/text-edit-diagnostics";
import { readImageEditStatus, waitForImageEditCheck, checkImageEditNow } from "@/lib/image-edit-observation";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Paintbrush, Check, RotateCcw, Eraser, Lasso, MousePointerClick, Undo2, X, HelpCircle, Wand2, Lightbulb, ImagePlus, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  rememberPendingImageEdit,
  findPendingImageEdit,
  clearPendingImageEdit,
  imageEditPhaseLabel,
  type PendingImageEdit,
  updatePendingImageEdit, persistPendingImageEdit,
  beginImageEditSubmission, endImageEditSubmission, isImageEditSubmitting,
  setImageEditModalOwner, isImageEditSaving,
  imageEditOwnerKey,
} from "@/lib/pending-image-edits";
import { usePendingImageEditsRevision } from "@/hooks/use-pending-image-edits";
import { suggestedMaskToolForGoal } from "@/lib/edit-goal-tool";

interface InpaintingModalProps {
  onEditFailed?: (message: string) => void;
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  // May be async. The modal stays open (showing a "saving" state) until
  // the returned promise resolves, and keeps itself open with the thrown
  // error message if it rejects — so callers that persist on accept get
  // real success/failure feedback instead of a modal that always closes.
  onAccept: (newImageUrl: string) => void | Promise<void>;
  // Optional product packaging/reference images. When provided, they are
  // forwarded to the edit endpoint so the edit model keeps the product
  // packaging faithful to the real product while applying the change.
  referenceImageUrls?: string[];
  // Optional generated-ad id the edited image belongs to. When provided, the
  // server resolves whether that ad is logo-bearing and runs a fail-open
  // wrong-logo QA check on the edit result (Task #1075). Omit for images
  // that aren't persisted ads — the edit then skips logo QA entirely.
  adId?: string;
  editTarget?: PendingImageEdit["target"];
  // Brand the edited image belongs to. Required for the region-selection
  // tools: they're gated behind the `region_masked_image_edit` beta feature
  // (a user grant for this brand, Admin → Beta Features). No brandId or no
  // grant = the modal shows only the legacy whole-image edit flow.
  brandId?: string;
  // Task #1484 — layout-only mode. When provided, the modal keeps its look
  // (large image + instruction box + Submit) but delegates the submit to this
  // handler instead of the built-in /api/edit-image job flow, closing itself
  // on successful kickoff. Region-masking tools, SAM click-to-select, and
  // reference-image uploads are hidden regardless of beta grants (the beta
  // query isn't even fetched). Absent = existing behavior, byte-identical.
  simpleSubmit?: (instructions: string) => Promise<void>;
  // Optional header copy overrides (used with simpleSubmit).
  title?: string;
  description?: string;
}

import LayeredEditEntry from "./layered-edit-entry";

export function InpaintingModal({ open, onClose, imageUrl, onAccept, onEditFailed, referenceImageUrls, adId, brandId, editTarget, simpleSubmit, title, description }: InpaintingModalProps) {
  const trackerRevision = usePendingImageEditsRevision();
  const ownerKey = imageEditOwnerKey(imageUrl, editTarget);
  const activeJobRef = useRef<string | null>(null);
  const submittingRef = useRef(false);
  const pollingRef = useRef(false);
  const preparingNextEditRef = useRef(false);
  const onEditFailedRef = useRef(onEditFailed);
  onEditFailedRef.current = onEditFailed;
  const isSimpleMode = !!simpleSubmit;
  const [prompt, setPrompt] = useState("");
  const [layeredMode, setLayeredMode] = useState(false);
  const [layeredQuality, setLayeredQuality] = useState<PendingImageEdit["layeredQuality"]>();
  const layeredScopeRef = useRef("");
  const layeredScope = `${brandId ?? ""}:${adId ?? ""}:${imageUrl}`;
  layeredScopeRef.current = layeredScope;
  useEffect(() => { setLayeredMode(false); setLayeredQuality(undefined); }, [layeredScope]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resultImageUrl, setResultImageUrl] = useState<string | null>(null);
  // A completed preview is not silently made the next input. "Edit this
  // result" explicitly promotes it, while Try Again always returns to the
  // original prop image and preserves its established retry behavior.
  const [continuedSourceUrl, setContinuedSourceUrl] = useState<string | null>(null);
  const sourceForEdit = continuedSourceUrl || imageUrl;
  useEffect(() => {
    setContinuedSourceUrl(null);
  }, [imageUrl]);
  // Coarse server-reported progress phase while an edit is in flight
  // (rendering → saving); null until the first poll lands.
  const [editPhase, setEditPhase] = useState<string | null>(null);

  // ── Optional region mask (paint-to-select) ────────────────────────────────
  // The user can toggle "Select area" and paint over the image with an
  // adjustable brush; only the painted region is edited (approximately —
  // region targeting follows the model's mask, not pixel surgery). The mask
  // canvas is kept at the image's NATURAL pixel dimensions and scaled down
  // via CSS, so the exported mask PNG matches the real image resolution
  // without a resample step client-side.
  const [maskingEnabled, setMaskingEnabled] = useState(false);
  // Beta gate: region-selection tools only for users granted
  // `region_masked_image_edit` on this brand. Default (no grant / no brand
  // context / query still loading) = hidden, legacy whole-image edit only.
  const { data: betaFlags, isError: featureResolutionFailed } = useQuery<Record<string, boolean>>({
    queryKey: [`/api/brands/${brandId}/beta-features`],
    enabled: open && !!brandId && !isSimpleMode,
  });
  const regionEditingAllowed = !isSimpleMode && !!brandId && betaFlags?.region_masked_image_edit === true;
  const layeredAllowed = !featureResolutionFailed && !isSimpleMode && !!brandId && !!adId && betaFlags?.ad_delayer_layered_edit === true;
  // Task #1971 — per-region "Remove" / "Edit text" quick actions, gated on
  // their own grant. No grant = no quick-action UI and the submit never
  // carries region_intent (freeform payload, byte-identical).
  const quickActionsAllowed = regionEditingAllowed && betaFlags?.region_edit_quick_actions === true;
  // Task #1972 — goal picker for masked edits, gated on its own grant. The
  // grant also authorizes the removal/text_edit intent bypass (it consumes
  // the Task #1971 plumbing) plus the attribute_edit override server-side.
  // No grant = no picker; the masked-edit flow is byte-identical to today.
  const goalPickerAllowed = regionEditingAllowed && betaFlags?.masked_edit_goal_picker === true;
  // Per-selection quick-action controls render for EITHER grant (the goal
  // picker needs them visible so a goal-defaulted intent can be overridden).
  const quickActionControlsAllowed = quickActionsAllowed || goalPickerAllowed;
  // Task #1432 — user-uploaded reference images, gated behind the
  // `edit_reference_images` beta grant. No grant / no brand = the control is
  // hidden and nothing new rides the submit (legacy request, byte-identical).
  const userRefsAllowed = !isSimpleMode && !!brandId && betaFlags?.edit_reference_images === true;
  const MAX_USER_REFS = 3;
  // Each ref is uploaded to durable storage IMMEDIATELY on select; only the
  // returned durable URL is kept (and later sent on submit). `purpose` +
  // `note` mirror the Ads Express ad-generation reference model: the user
  // picks how the reference should be used (or leaves it on Auto — the
  // server detects product shot vs. scene at submit) and can add a short
  // note about what to pull from it.
  type UserRefPurpose = "auto" | "environment" | "product" | "product_environment";
  interface UserRef {
    id: number;
    status: "uploading" | "ready";
    url: string | null; // durable URL once ready
    previewUrl: string; // local object URL for the thumbnail
    purpose: UserRefPurpose;
    note: string;
  }
  const [userRefs, setUserRefs] = useState<UserRef[]>([]);
  const userRefIdRef = useRef(0);
  const refFileInputRef = useRef<HTMLInputElement | null>(null);
  const anyRefUploading = userRefs.some((r) => r.status === "uploading");
  // Reference-only submits: with at least one uploaded reference the typed
  // description becomes optional (the server synthesizes an instruction from
  // each reference's "Use as" purpose, mirroring Ads Express).
  const readyUserRefCount = userRefsAllowed
    ? userRefs.filter((r) => r.status === "ready" && r.url).length
    : 0;
  // If the grant disappears while refs are attached, drop them so a submit
  // can't send a field the server would reject.
  useEffect(() => {
    if (!userRefsAllowed && userRefs.length > 0) {
      setUserRefs((prev) => {
        for (const r of prev) URL.revokeObjectURL(r.previewUrl);
        return [];
      });
    }
  }, [userRefsAllowed, userRefs.length]);

  const handleRefFileSelected = useCallback(
    async (file: File | undefined | null) => {
      if (!file) return;
      if (!/^image\/(png|jpeg|webp)$/.test(file.type)) {
        setError("Reference images must be PNG, JPEG, or WebP.");
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError("Reference images must be under 10 MB.");
        return;
      }
      setError(null);
      const id = ++userRefIdRef.current;
      const previewUrl = URL.createObjectURL(file);
      setUserRefs((prev) =>
        prev.length >= MAX_USER_REFS
          ? prev
          : [...prev, { id, status: "uploading", url: null, previewUrl, purpose: "auto", note: "" }],
      );
      try {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result));
          reader.onerror = () => reject(new Error("Couldn't read the file"));
          reader.readAsDataURL(file);
        });
        const res = await fetch("/api/edit-image/reference-upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            image_data_url: dataUrl,
            brandId: brandId || undefined,
            adId: adId || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data?.url) {
          throw new Error((data as any)?.message || "Reference upload failed");
        }
        setUserRefs((prev) =>
          prev.map((r) => (r.id === id ? { ...r, status: "ready" as const, url: data.url as string } : r)),
        );
      } catch (e: any) {
        setUserRefs((prev) => prev.filter((r) => r.id !== id));
        URL.revokeObjectURL(previewUrl);
        setError(e?.message || "Reference upload failed");
      }
    },
    [brandId, adId],
  );

  const removeUserRef = useCallback((id: number) => {
    setUserRefs((prev) => {
      const gone = prev.find((r) => r.id === id);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((r) => r.id !== id);
    });
  }, []);
  const updateUserRefPurpose = useCallback((id: number, purpose: UserRefPurpose) => {
    setUserRefs((prev) => prev.map((r) => (r.id === id ? { ...r, purpose } : r)));
  }, []);
  const updateUserRefNote = useCallback((id: number, note: string) => {
    setUserRefs((prev) => prev.map((r) => (r.id === id ? { ...r, note } : r)));
  }, []);
  // If the grant disappears (or never loads) while selecting, fall back to
  // the whole-image flow so a submit can't send a mask the server rejects.
  useEffect(() => {
    if (!regionEditingAllowed && maskingEnabled) setMaskingEnabled(false);
  }, [regionEditingAllowed, maskingEnabled]);
  // Task #1591 — Select area starts ON for region-enabled brands. The beta
  // flag loads async, so this fires once per modal open when the flag
  // resolves to enabled; the ref keeps it from re-enabling after the user
  // toggles it off in the same session. No flag = untouched legacy default.
  const maskDefaultAppliedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      maskDefaultAppliedRef.current = false;
      return;
    }
    if (regionEditingAllowed && !maskDefaultAppliedRef.current) {
      maskDefaultAppliedRef.current = true;
      setMaskingEnabled(true);
    }
  }, [open, regionEditingAllowed]);
  const [brushSize, setBrushSize] = useState(40); // in displayed (CSS) pixels
  // Selection tool: freehand brush (default) or click-to-select, which sends
  // the clicked point to the server (SAM 2 segmentation). Each click or brush
  // STROKE becomes its own numbered selection with its OWN comment — all of
  // them are applied together on one Submit.
  const [maskTool, setMaskTool] = useState<"brush" | "click">("brush");
  // Task #1972 — the selected GOAL (independent of the mask tool and of any
  // in-progress selections: switching tools never resets the goal, switching
  // goals never resets a mask). null = no goal picked — byte-identical to
  // the freeform flow, same as "describe". Selecting a goal SUGGESTS a
  // default tool (never a lock-out) and pre-sets the per-region intent for
  // selections made afterwards.
  type EditGoal = "remove" | "edit_text" | "style" | "describe";
  const [editGoal, setEditGoal] = useState<EditGoal | null>(null);
  // Read by the async selection handlers so a click resolving late still
  // sees the goal that is CURRENTLY selected.
  const editGoalRef = useRef<EditGoal | null>(null);
  editGoalRef.current = editGoal;
  // Default per-region intent a goal implies for NEW selections. Existing
  // selections keep whatever they had — goals never rewrite them.
  const goalDefaultQuickAction = useCallback(
    (goal: EditGoal | null): "removal" | "text_edit" | "attribute_edit" | null => {
      if (!goalPickerAllowed) return null;
      if (goal === "remove") return "removal";
      if (goal === "edit_text") return "text_edit";
      if (goal === "style") return "attribute_edit";
      return null;
    },
    [goalPickerAllowed],
  );
  const selectGoal = useCallback((goal: EditGoal) => {
    setEditGoal((prev) => {
      if (prev === goal) return null; // tap the active goal to deselect
      // Suggest (never force) the goal's default tool. Task #2040 —
      // "Remove something" and "Describe it" keep whatever tool is active
      // (removal works via the intent tag regardless of tool); only
      // text/style still suggest the brush.
      setMaskTool((tool) => suggestedMaskToolForGoal(goal, tool));
      return goal;
    });
  }, []);
  // Per-selection annotations. Each click / brush stroke is one entry with its
  // own offscreen natural-dims mask canvas and its own comment. A click starts
  // as "pending" (numbered marker + spinner row) while SAM 2 runs — clicks are
  // NON-blocking, so several can be in flight while the user keeps selecting.
  interface MaskAnnotation {
    id: number;
    kind: "brush" | "click";
    status: "pending" | "ready";
    comment: string;
    maskCanvas: HTMLCanvasElement | null; // null only while pending
    // Marker anchor in natural-pixel coords (click point / stroke centroid).
    markerX: number;
    markerY: number;
    // Per-selection reference picking: which uploaded reference images (by
    // UserRef id) apply to THIS selection. null = all of them (the default —
    // the submit omits ref_indices, keeping the legacy payload shape). A
    // list that grows back to every ref normalizes to null again.
    refIds: number[] | null;
    // Task #1971 — quick action for this selection. null = freeform comment
    // (the legacy contract). "removal" needs no text (comment doubles as the
    // optional note); "text_edit" uses currentText/newText below.
    // Task #1972 — "attribute_edit" (goal picker's "Change color or style"):
    // routing override only, the comment stays the user's own prompt.
    quickAction: "removal" | "text_edit" | "attribute_edit" | null;
    currentText: string;
    newText: string;
  }
  const MAX_ANNOTATIONS = 6;
  const [annotations, setAnnotations] = useState<MaskAnnotation[]>([]);
  const annotationIdRef = useRef(0);
  // Bumped on close/unmount/new-image so an in-flight SAM 2 fetch resolving
  // late can never write into dead or reset state (same idea as pollTokenRef
  // for the edit-job poller below).
  const maskSessionRef = useRef(0);
  const [naturalDims, setNaturalDims] = useState<{ w: number; h: number } | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);
  // The in-progress brush stroke draws into BOTH the display canvas (live
  // feedback) and this offscreen working canvas (so the finished stroke can
  // become its own annotation without re-deriving its pixels).
  const workingStrokeCanvasRef = useRef<HTMLCanvasElement | null>(null);
  // Full point trail of the in-progress stroke (natural-pixel coords) plus the
  // natural-per-CSS-px scale captured at stroke start — used at stroke end to
  // detect a closed loop and fill its interior (see handlePointerUp).
  const strokePointsRef = useRef<{ x: number; y: number }[]>([]);
  const strokeScaleRef = useRef(1);

  // Size the mask canvas to the image's natural dimensions once it loads (or
  // when the source image changes). Also clears any prior selections.
  const syncCanvasToImage = useCallback(() => {
    const img = imgRef.current;
    const canvas = maskCanvasRef.current;
    if (!img || !canvas || !img.naturalWidth || !img.naturalHeight) return;
    if (canvas.width !== img.naturalWidth || canvas.height !== img.naturalHeight) {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      maskSessionRef.current++;
      setAnnotations([]);
      setNaturalDims({ w: img.naturalWidth, h: img.naturalHeight });
    }
  }, []);

  const clearMask = useCallback(() => {
    maskSessionRef.current++; // discard any in-flight click selections
    setAnnotations([]);
  }, []);

  const resetMaskState = useCallback(() => {
    clearMask();
    setMaskingEnabled(false);
    setMaskTool("brush");
    setEditGoal(null);
  }, [clearMask]);

  // A different displayed image (prop change while open, or an accepted edit
  // result swapping in) invalidates every selection — clear annotations and
  // bump the session so a late SAM 2 response can't attach to the new image.
  // syncCanvasToImage alone misses this when the new image has IDENTICAL
  // dimensions to the old one.
  const displayedSrc = resultImageUrl || sourceForEdit;
  useEffect(() => {
    clearMask();
  }, [displayedSrc, clearMask]);

  // Map a pointer event to natural-pixel canvas coordinates.
  const pointFromEvent = useCallback((e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
      scale: canvas.width / rect.width,
    };
  }, []);

  // Undo = remove the LAST selection (a whole click or brush stroke); Clear
  // removes them all. No canvas snapshots needed — the composite is redrawn
  // from the per-annotation mask canvases whenever the list changes.
  const undoLastAction = useCallback(() => {
    setAnnotations((prev) => prev.slice(0, -1));
  }, []);

  const removeAnnotation = useCallback((id: number) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const setAnnotationComment = useCallback((id: number, comment: string) => {
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, comment } : a)));
  }, []);

  // Task #1971 — quick-action controls. Selecting the active action again
  // toggles back to the freeform comment; the freeform comment text is kept
  // so switching modes never loses what the user typed.
  const setAnnotationQuickAction = useCallback((id: number, action: "removal" | "text_edit" | "attribute_edit" | null) => {
    setAnnotations((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, quickAction: a.quickAction === action ? null : action } : a,
      ),
    );
  }, []);
  const setAnnotationCurrentText = useCallback((id: number, currentText: string) => {
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, currentText } : a)));
  }, []);
  const setAnnotationNewText = useCallback((id: number, newText: string) => {
    setAnnotations((prev) => prev.map((a) => (a.id === id ? { ...a, newText } : a)));
  }, []);

  // Toggle one reference image on/off for one selection. refIds === null
  // means "all references" (the default), so toggling first materializes the
  // full id list, then adds/removes; a list covering every uploaded ref
  // normalizes back to null so the submit omits ref_indices (legacy shape).
  const toggleAnnotationRef = useCallback(
    (annotationId: number, refId: number) => {
      setAnnotations((prev) =>
        prev.map((a) => {
          if (a.id !== annotationId) return a;
          const allIds = userRefs.map((r) => r.id);
          const current = a.refIds === null ? allIds : a.refIds;
          const next = current.includes(refId)
            ? current.filter((x) => x !== refId)
            : [...current, refId];
          const normalized = allIds.every((x) => next.includes(x)) ? null : next;
          return { ...a, refIds: normalized };
        }),
      );
    },
    [userRefs],
  );

  // Build an offscreen natural-dims canvas from a server-returned object mask
  // (hard B/W PNG, white = object). The annotation canvases encode "selected"
  // as any-alpha, so the mask's BLACK background must never be painted — only
  // white-opaque pixels where the mask is white. Also reports whether ANY
  // pixel was selected and the selection's centroid (for the numbered marker).
  const buildMaskCanvasFromDataUrl = useCallback(
    async (
      maskDataUrl: string,
    ): Promise<{ canvas: HTMLCanvasElement; any: boolean; cx: number; cy: number }> => {
      const base = maskCanvasRef.current;
      const W = base?.width || imgRef.current?.naturalWidth || 0;
      const H = base?.height || imgRef.current?.naturalHeight || 0;
      if (!W || !H) throw new Error("The selection couldn't be read. Try again.");
      const img = new Image();
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve();
        img.onerror = () => reject(new Error("The selection couldn't be read. Try again."));
        img.src = maskDataUrl;
      });
      const tmp = document.createElement("canvas");
      tmp.width = W;
      tmp.height = H;
      const tctx = tmp.getContext("2d");
      if (!tctx) throw new Error("The selection couldn't be read. Try again.");
      tctx.drawImage(img, 0, 0, W, H);
      const maskData = tctx.getImageData(0, 0, W, H).data;
      const out = document.createElement("canvas");
      out.width = W;
      out.height = H;
      const octx = out.getContext("2d");
      if (!octx) throw new Error("The selection couldn't be read. Try again.");
      const outImg = octx.createImageData(W, H);
      let any = false;
      let sumX = 0;
      let sumY = 0;
      let count = 0;
      for (let i = 0; i < maskData.length; i += 4) {
        if (maskData[i] > 127) {
          outImg.data[i] = 255;
          outImg.data[i + 1] = 255;
          outImg.data[i + 2] = 255;
          outImg.data[i + 3] = 255;
          any = true;
          const px = (i / 4) % W;
          const py = Math.floor(i / 4 / W);
          sumX += px;
          sumY += py;
          count++;
        }
      }
      octx.putImageData(outImg, 0, 0);
      return {
        canvas: out,
        any,
        cx: count > 0 ? sumX / count : W / 2,
        cy: count > 0 ? sumY / count : H / 2,
      };
    },
    [],
  );

  // Click-to-select: a pending numbered selection appears at the clicked spot
  // immediately and SAM 2 runs in the background — the canvas stays fully
  // interactive, so several clicks (or brush strokes) can be made while
  // selections are still resolving. A failed/empty selection removes its
  // pending entry and surfaces the error.
  const handleSegmentClick = useCallback(
    async (e: React.PointerEvent<HTMLCanvasElement>) => {
      const p = pointFromEvent(e);
      if (!p) return;
      const session = maskSessionRef.current;
      const id = ++annotationIdRef.current;
      setError(null);
      setAnnotations((prev) => [
        ...prev,
        {
          id,
          kind: "click",
          status: "pending",
          comment: "",
          maskCanvas: null,
          markerX: p.x,
          markerY: p.y,
          refIds: null,
          // Task #1972 — a selected goal pre-sets this selection's intent
          // (null without the grant / without a goal — legacy behavior).
          quickAction: goalDefaultQuickAction(editGoalRef.current),
          currentText: "",
          newText: "",
        },
      ]);
      try {
        const res = await fetch("/api/edit-image/segment", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            image_url: sourceForEdit,
            x: Math.round(p.x),
            y: Math.round(p.y),
            brandId: brandId || undefined,
            adId: adId || undefined,
          }),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          const err: any = new Error((data as any).message || "Couldn't select an object at that spot.");
          // 400 = the existing SOURCE_NOT_ALLOWED / BAD_POINT "can't be
          // segmented" outcome (the route collapses both onto one 400).
          err.cantSegment = res.status === 400;
          throw err;
        }
        const { mask_data_url } = (await res.json()) as { mask_data_url?: string };
        if (!mask_data_url) throw new Error("Couldn't select an object at that spot.");
        const built = await buildMaskCanvasFromDataUrl(mask_data_url);
        // Stale session (modal closed / image changed / cleared): drop silently.
        if (maskSessionRef.current !== session) return;
        if (!built.any) {
          setAnnotations((prev) => prev.filter((a) => a.id !== id));
          setError("Nothing was found at that spot — try clicking the object's center, or use the brush.");
          return;
        }
        setAnnotations((prev) =>
          prev.map((a) =>
            a.id === id
              ? { ...a, status: "ready" as const, maskCanvas: built.canvas, markerX: built.cx, markerY: built.cy }
              : a,
          ),
        );
      } catch (err: any) {
        if (maskSessionRef.current !== session) return;
        setAnnotations((prev) => prev.filter((a) => a.id !== id));
        // Task #1972 — on a click-defaulted goal, a "can't be segmented"
        // failure auto-flips the tool toggle to brush (goal, existing
        // selections, and mask state all preserved) instead of only telling
        // the user to switch. Any other failure keeps the current tool.
        if (
          err?.cantSegment === true &&
          goalPickerAllowed &&
          editGoalRef.current === "remove"
        ) {
          setMaskTool("brush");
          setError(
            "That spot couldn't be selected automatically — switched to the brush. Paint over the area instead (your other selections are kept).",
          );
          return;
        }
        setError(err?.message || "Couldn't select an object at that spot.");
      }
    },
    [pointFromEvent, sourceForEdit, buildMaskCanvasFromDataUrl, brandId, adId, goalPickerAllowed, goalDefaultQuickAction],
  );

  // Draw one stroke segment into BOTH the display canvas (live feedback) and
  // the offscreen working canvas (which becomes the stroke's own mask).
  const drawStroke = useCallback(
    (from: { x: number; y: number }, to: { x: number; y: number }, scale: number) => {
      const targets = [
        maskCanvasRef.current?.getContext("2d"),
        workingStrokeCanvasRef.current?.getContext("2d"),
      ];
      for (const ctx of targets) {
        if (!ctx) continue;
        ctx.strokeStyle = "#ffffff";
        ctx.fillStyle = "#ffffff";
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.lineWidth = Math.max(1, brushSize * scale);
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
      }
    },
    [brushSize],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!maskingEnabled || isSubmitting) return;
      if (annotations.length >= MAX_ANNOTATIONS) {
        setError(`Up to ${MAX_ANNOTATIONS} selections per edit — remove one to add another.`);
        return;
      }
      if (maskTool === "click") {
        // Click-to-select: one click = one selection (async, NON-blocking —
        // more clicks/strokes can be made while it resolves).
        void handleSegmentClick(e);
        return;
      }
      const p = pointFromEvent(e);
      if (!p) return;
      const display = maskCanvasRef.current;
      if (!display || !display.width || !display.height) return;
      // Fresh offscreen working canvas for THIS stroke only.
      const working = document.createElement("canvas");
      working.width = display.width;
      working.height = display.height;
      workingStrokeCanvasRef.current = working;
      e.currentTarget.setPointerCapture(e.pointerId);
      isDrawingRef.current = true;
      lastPointRef.current = { x: p.x, y: p.y };
      strokePointsRef.current = [{ x: p.x, y: p.y }];
      strokeScaleRef.current = p.scale;
      // Dot for a single click.
      drawStroke({ x: p.x, y: p.y }, { x: p.x + 0.01, y: p.y + 0.01 }, p.scale);
    },
    [maskingEnabled, isSubmitting, annotations.length, maskTool, handleSegmentClick, pointFromEvent, drawStroke],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (!isDrawingRef.current) return;
      const p = pointFromEvent(e);
      if (!p || !lastPointRef.current) return;
      drawStroke(lastPointRef.current, { x: p.x, y: p.y }, p.scale);
      lastPointRef.current = { x: p.x, y: p.y };
      strokePointsRef.current.push({ x: p.x, y: p.y });
    },
    [pointFromEvent, drawStroke],
  );

  // Users "circle" an object expecting the whole object to be selected — but
  // raw strokes alone would mask only the thin outline, so the model edits the
  // ring and leaves the circled object untouched (real user-reported failure).
  // Two complementary fills run at stroke end so the selection visibly fills in:
  //
  // 1. Loop fill from stroke GEOMETRY: if the stroke's endpoints come back
  //    near each other, fill the closed polygon it traced. Because this uses
  //    the actual path (not pixels), it also works when the loop is clipped by
  //    the image edge — the canvas clips the fill for us. Deliberate open
  //    strokes (paint-over scribbles, arcs) don't trigger it.
  // 2. Raster fill of fully-enclosed regions: flood from the canvas borders
  //    across unpainted pixels; anything unreached is surrounded by paint
  //    (e.g. a loop closed across SEVERAL strokes) and becomes selected.
  //
  // The server applies the same raster fill defensively during normalization.
  const fillEnclosedRegions = useCallback((canvas: HTMLCanvasElement | null) => {
    if (!canvas || !canvas.width || !canvas.height) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width;
    const H = canvas.height;
    const img = ctx.getImageData(0, 0, W, H);
    const d = img.data;
    const n = W * H;
    // 0 = unpainted & unvisited, 1 = painted, 2 = unpainted & border-connected.
    const state = new Uint8Array(n);
    for (let i = 0; i < n; i++) if (d[i * 4 + 3] > 0) state[i] = 1;
    const stack = new Int32Array(n);
    let sp = 0;
    const push = (i: number) => {
      if (state[i] === 0) {
        state[i] = 2;
        stack[sp++] = i;
      }
    };
    for (let x = 0; x < W; x++) {
      push(x);
      push((H - 1) * W + x);
    }
    for (let y = 0; y < H; y++) {
      push(y * W);
      push(y * W + W - 1);
    }
    while (sp > 0) {
      const i = stack[--sp];
      const x = i % W;
      if (x > 0) push(i - 1);
      if (x < W - 1) push(i + 1);
      if (i >= W) push(i - W);
      if (i < n - W) push(i + W);
    }
    let filled = false;
    for (let i = 0; i < n; i++) {
      if (state[i] === 0) {
        filled = true;
        const o = i * 4;
        d[o] = 255;
        d[o + 1] = 255;
        d[o + 2] = 255;
        d[o + 3] = 255;
      }
    }
    if (filled) ctx.putImageData(img, 0, 0);
  }, []);

  const handlePointerUp = useCallback(() => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    lastPointRef.current = null;
    const pts = strokePointsRef.current;
    strokePointsRef.current = [];
    const working = workingStrokeCanvasRef.current;
    workingStrokeCanvasRef.current = null;
    if (!working) return;
    const wctx = working.getContext("2d");
    if (wctx && pts.length >= 8) {
      let pathLen = 0;
      for (let i = 1; i < pts.length; i++) {
        pathLen += Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y);
      }
      const endGap = Math.hypot(
        pts[0].x - pts[pts.length - 1].x,
        pts[0].y - pts[pts.length - 1].y,
      );
      const brushNatural = Math.max(1, brushSize * strokeScaleRef.current);
      // Loop-ish: long enough to enclose something, and the endpoints come
      // back near each other (within a couple of brush widths, or 15% of the
      // path length for big sweeping circles).
      if (pathLen > brushNatural * 4 && endGap <= Math.max(brushNatural * 2.5, pathLen * 0.15)) {
        wctx.fillStyle = "#ffffff";
        wctx.beginPath();
        wctx.moveTo(pts[0].x, pts[0].y);
        for (let i = 1; i < pts.length; i++) wctx.lineTo(pts[i].x, pts[i].y);
        wctx.closePath();
        wctx.fill("nonzero");
      }
    }
    // Loop/enclosed fills run on the stroke's OWN canvas; the composite
    // effect below redraws the display canvas from all annotation canvases.
    fillEnclosedRegions(working);
    // Marker anchor = stroke centroid (average of the trail points).
    let cx = 0;
    let cy = 0;
    for (const p of pts) {
      cx += p.x;
      cy += p.y;
    }
    cx = pts.length ? cx / pts.length : working.width / 2;
    cy = pts.length ? cy / pts.length : working.height / 2;
    const id = ++annotationIdRef.current;
    setAnnotations((prev) =>
      prev.length >= MAX_ANNOTATIONS
        ? prev
        : [
            ...prev,
            {
              id,
              kind: "brush" as const,
              status: "ready" as const,
              comment: "",
              maskCanvas: working,
              markerX: cx,
              markerY: cy,
              refIds: null,
              // Task #1972 — goal-defaulted intent (null without a goal).
              quickAction: goalDefaultQuickAction(editGoalRef.current),
              currentText: "",
              newText: "",
            },
          ],
    );
  }, [brushSize, fillEnclosedRegions, goalDefaultQuickAction]);

  // Composite render: whenever the annotation list changes, redraw the display
  // canvas from every annotation's own mask canvas (the in-progress stroke is
  // drawn live on top by drawStroke). Removing/undoing a selection therefore
  // visibly erases exactly that selection's pixels.
  useEffect(() => {
    const canvas = maskCanvasRef.current;
    if (!canvas || !canvas.width || !canvas.height) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (const a of annotations) {
      if (a.maskCanvas) ctx.drawImage(a.maskCanvas, 0, 0);
    }
  }, [annotations]);

  // Export ONE annotation's mask canvas as a hard black/white mask PNG at the
  // image's natural dimensions (white = region to edit, black = preserve).
  // The server re-validates and re-normalizes against the real source bytes.
  const exportMaskDataUrl = useCallback((canvas: HTMLCanvasElement | null): string | null => {
    if (!canvas || !canvas.width || !canvas.height) return null;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    const src = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let painted = false;
    const out = document.createElement("canvas");
    out.width = canvas.width;
    out.height = canvas.height;
    const outCtx = out.getContext("2d");
    if (!outCtx) return null;
    const outData = outCtx.createImageData(canvas.width, canvas.height);
    for (let i = 0; i < src.data.length; i += 4) {
      const on = src.data[i + 3] > 0;
      if (on) painted = true;
      const v = on ? 255 : 0;
      outData.data[i] = v;
      outData.data[i + 1] = v;
      outData.data[i + 2] = v;
      outData.data[i + 3] = 255;
    }
    if (!painted) return null;
    outCtx.putImageData(outData, 0, 0);
    return out.toDataURL("image/png");
  }, []);

  // Cancellation token for the poll loop. Bumped whenever a poller should stop
  // surfacing results into THIS component — on close, on unmount, and when a new
  // poll supersedes an old one. A poller whose token is stale exits silently
  // WITHOUT clearing the persisted pending job, so the edit (still running
  // server-side) can be re-attached when the user returns. This is the crucial
  // guard: a stale loop from a closed/unmounted modal must never clear the
  // localStorage entry or write to dead state.
  const pollTokenRef = useRef(0);
  useEffect(() => {
    if (!open || isSimpleMode) return;
    setImageEditModalOwner(ownerKey, true);
    return () => {
      pollTokenRef.current++;
      pollingRef.current = false;
      setImageEditModalOwner(ownerKey, false);
    };
  }, [open, ownerKey, isSimpleMode]);

  useEffect(() => {
    if (!open) {
      // Closing supersedes any in-flight poller (leaves the pending job intact
      // for re-attach) and resets the transient UI.
      pollTokenRef.current++;
      preparingNextEditRef.current = false;
      setPrompt("");
      setLayeredMode(false);
      setError(null);
      setResultImageUrl(null);
      setContinuedSourceUrl(null);
      setIsAccepting(false);
      setIsSubmitting(false);
      setEditPhase(null);
      resetMaskState();
      // Task #1432 — drop attached reference images (revoking their local
      // preview URLs); the durable uploads are cheap and simply unused.
      setUserRefs((prev) => {
        for (const r of prev) URL.revokeObjectURL(r.previewUrl);
        return [];
      });
    }
  }, [open, resetMaskState]);

  // Cancel any in-flight poller when the component unmounts (e.g. the user
  // navigates away mid-edit) so it can't clear the pending job or write state
  // after teardown.
  useEffect(() => {
    return () => {
      pollTokenRef.current++;
      maskSessionRef.current++;
    };
  }, []);

  // Poll a (possibly already-running) edit job to completion. The jobId is
  // persisted in localStorage while in flight so that if the modal is closed
  // mid-edit, reopening it re-attaches to the SAME job instead of re-submitting
  // (the edit keeps running server-side regardless). Ready/error records stay
  // recoverable for explicit review or save retry, not implicit acceptance.
  const pollEditJob = useCallback(async (jobId: string) => {
    activeJobRef.current = jobId;
    pollingRef.current = true;
    const myToken = ++pollTokenRef.current;
    setIsSubmitting(true);
    setError(null);

    let outcome: { type: "ready"; url: string } | { type: "failed"; message: string; diagnostic: TextEditDiagnostic | null };
    while (true) {
      if (pollTokenRef.current !== myToken) return; // superseded/cancelled — leave pending intact
      try {
        const pollRes = await readImageEditStatus(jobId);
        if (pollRes.ok) {
          const status = (await pollRes.json()) as {
            status: "pending" | "ready" | "failed";
            image_url?: string;
            message?: string;
            phase?: string;
            diagnostic?: unknown;
          };
          if (status.status === "pending" && pollTokenRef.current === myToken) {
            setEditPhase(status.phase ?? null);
          }
          if (status.status === "ready" && status.image_url) {
            outcome = { type: "ready", url: status.image_url };
            break;
          }
          if (status.status === "failed") {
            outcome = { type: "failed", message: status.message || "Image edit failed",
              diagnostic: safeTextEditDiagnostic(status.diagnostic) };
            break;
          }
        }
      } catch {
        if (pollTokenRef.current !== myToken) return;
        setEditPhase("checking");
      }
      await waitForImageEditCheck(jobId);
    }

    // Apply terminal effects only if this poller is still the active one. A
    // stale poller (modal closed/unmounted, or superseded) must not clear the
    // pending job or write into dead state.
    if (pollTokenRef.current !== myToken) return;
    if (outcome.type === "ready") {
      // A visible preview is not acceptance. Keep it for reopen/save retry.
      updatePendingImageEdit(jobId, { delivery: "review", resultImageUrl: outcome.url, error: undefined });
      setResultImageUrl(outcome.url);
    } else if (outcome.type === "failed") {
      updatePendingImageEdit(jobId, { delivery: "review", error: outcome.message, diagnostic: outcome.diagnostic });
      setError(outcome.message);
      onEditFailedRef.current?.(outcome.message);
    }
    setIsSubmitting(false);
    pollingRef.current = false;
    setEditPhase(null);
  }, []);

  // Derived selection state. Regions apply only while masking is ON (matching
  // the old single-mask behavior: toggling "Select area" off falls back to a
  // whole-image edit with the global prompt, selections kept for re-enable).
  const activeAnnotations = maskingEnabled ? annotations : [];
  const anyAnnotationPending = activeAnnotations.some((a) => a.status === "pending");
  const readyAnnotations = activeAnnotations.filter((a) => a.status === "ready");
  const usingRegions = readyAnnotations.length > 0 || anyAnnotationPending;
  // Task #1971 — a "Remove" quick action needs no text; "Edit text" needs the
  // new text; freeform selections need a comment (the legacy rule).
  const annotationInputsFilled = (a: MaskAnnotation) =>
    a.quickAction === "removal"
      ? true
      : a.quickAction === "text_edit"
        ? a.newText.trim().length > 0
        : a.comment.trim().length > 0;
  const allCommentsFilled = readyAnnotations.every(annotationInputsFilled);
  const canSubmit = usingRegions
    ? !anyAnnotationPending && readyAnnotations.length > 0 && allCommentsFilled
    : prompt.trim().length > 0 || readyUserRefCount > 0;

  const handleSubmit = useCallback(async () => {
    // Task #1484 — layout-only mode: hand the instructions to the caller's
    // own submit flow (e.g. the meme edit endpoint) and close on success.
    // Nothing rides the built-in /api/edit-image job pipeline.
    if (simpleSubmit) {
      if (!prompt.trim()) {
        setError("Please describe what you want to change");
        return;
      }
      setIsSubmitting(true);
      setError(null);
      try {
        await simpleSubmit(prompt.trim());
        onClose();
      } catch (e: any) {
        setError(e?.message || "Could not start the edit");
      } finally {
        setIsSubmitting(false);
      }
      return;
    }
    // Per-selection mode: every click/stroke carries its own comment and the
    // server derives the prompt(s) — no global prompt is sent. Otherwise the
    // request body is byte-identical to the pre-annotation contract.
    const sendRegions = maskingEnabled && annotations.some((a) => a.status === "ready");
    if (sendRegions) {
      if (annotations.some((a) => a.status === "pending")) {
        setError("A selection is still resolving — one moment.");
        return;
      }
      if (!annotations.every(annotationInputsFilled)) {
        setError("Add a comment (or the new text for Edit text) to every selection before submitting.");
        return;
      }
    } else if (!prompt.trim() && readyUserRefCount === 0) {
      setError("Please describe what you want to change");
      return;
    }

    if (submittingRef.current || !beginImageEditSubmission(ownerKey, editTarget)) return;
    submittingRef.current = true;
    const submissionToken = pollTokenRef.current;
    setIsSubmitting(true);
    setError(null);

    try {
      // Freeze the visible input for this job. A completed-result continuation
      // must be keyed/polled against that result, even if the parent refreshes
      // its original-image prop while the request is in flight.
      const submissionSourceUrl = sourceForEdit;
      let regionsPayload:
        | {
            mask_data_url: string;
            comment: string;
            ref_indices?: number[];
            region_intent?: "removal" | "text_edit" | "attribute_edit";
            current_text?: string;
            new_text?: string;
          }[]
        | null = null;
      if (sendRegions) {
        // Per-selection reference picking: ref_indices point into the SAME
        // ready-refs array submitted as userReferences below (same filter,
        // same order). Omitted when a selection uses all refs (the default)
        // so the default payload stays byte-identical to the legacy shape.
        const readyRefList = userRefsAllowed
          ? userRefs.filter((r) => r.status === "ready" && r.url)
          : [];
        regionsPayload = [];
        for (const a of annotations) {
          const maskDataUrl = exportMaskDataUrl(a.maskCanvas);
          if (!maskDataUrl) {
            throw new Error("One of the selections is empty — remove it and try again.");
          }
          const entry: {
            mask_data_url: string;
            comment: string;
            ref_indices?: number[];
            region_intent?: "removal" | "text_edit" | "attribute_edit";
            current_text?: string;
            new_text?: string;
          } = {
            mask_data_url: maskDataUrl,
            comment: a.comment.trim(),
          };
          // Task #1971 — explicit quick-action override. Only sent when a
          // grant is active (the controls don't render otherwise); the
          // server composes the actual prompt from these structured fields.
          // Task #1972 — the goal-picker grant also authorizes these
          // overrides, and is the only grant for attribute_edit (whose
          // comment stays the user's own prompt — routing override only).
          if (quickActionControlsAllowed && a.quickAction === "removal") {
            entry.region_intent = "removal";
          } else if (quickActionControlsAllowed && a.quickAction === "text_edit") {
            entry.region_intent = "text_edit";
            entry.new_text = a.newText.trim();
            if (a.currentText.trim()) entry.current_text = a.currentText.trim();
            entry.comment = "";
          } else if (goalPickerAllowed && a.quickAction === "attribute_edit") {
            entry.region_intent = "attribute_edit";
          }
          if (a.refIds !== null && readyRefList.length > 0) {
            const allow = new Set(a.refIds);
            const indices = readyRefList
              .map((r, i) => (allow.has(r.id) ? i : -1))
              .filter((i) => i >= 0);
            // A stale refIds list that still covers every submitted ref is
            // the same as "all" — omit the field.
            if (indices.length < readyRefList.length) entry.ref_indices = indices;
          }
          regionsPayload.push(entry);
        }
      }

      // Submit the edit as a background job and poll for completion. Because the
      // edit no longer blocks an HTTP connection, a merely slow/busy Fal image
      // queue keeps working in the background instead of timing out — the result
      // appears when ready. Real failures still surface a precise message.
      const res = await fetch("/api/edit-image", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          image_url: submissionSourceUrl,
          ...(regionsPayload ? {} : { prompt: prompt.trim() }),
          referenceImageUrls: referenceImageUrls && referenceImageUrls.length > 0 ? referenceImageUrls : undefined,
          // Task #1432 — user-uploaded refs ride a DISTINCT field from the
          // parent-supplied product refs (gated independently server-side).
          // Structured entries ({url, purpose, note}) mirror the Ads Express
          // reference model; "auto" purposes are resolved server-side.
          userReferences: (() => {
            const ready = userRefs
              .filter((r) => r.status === "ready" && r.url)
              .map((r) => ({ url: r.url as string, purpose: r.purpose, note: r.note.trim() }));
            return userRefsAllowed && ready.length > 0 ? ready : undefined;
          })(),
          adId: adId || undefined,
          ...(regionsPayload
            ? { regions: regionsPayload, brandId: brandId || undefined }
            : !adId && brandId
              ? { brandId }
              : {}),
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // Task #1794 — every non-2xx submit (403 gate, 400 validation, 500)
        // surfaces its server message; when there is none, at least name the
        // status so the failure is never a silent no-op.
        throw new Error((data as any).message || `Image edit failed (${res.status}). Please try again.`);
      }
      const { jobId } = (await res.json()) as { jobId: string };
      if (!jobId) throw new Error("Image edit failed");

      // Persist the in-flight job keyed on the source image so closing the modal
      // mid-edit doesn't lose the result — reopening re-attaches to this job.
      // Root under the original modal image so reopening can find a continued
      // result edit, but preserve its real submitted source for preview and
      // server provenance.
      rememberPendingImageEdit(imageUrl, jobId, submissionSourceUrl, editTarget);
      // Registration is observable even after teardown. Never create a fresh
      // polling token in an abandoned modal; its live parent/reopen owns it.
      if (pollTokenRef.current !== submissionToken) return;
      await pollEditJob(jobId);
    } catch (e: any) {
      if (pollTokenRef.current !== submissionToken) return;
      setError(e?.message || "Image edit failed");
      setIsSubmitting(false);
    } finally {
      submittingRef.current = false;
      endImageEditSubmission(ownerKey);
    }
  }, [prompt, sourceForEdit, imageUrl, ownerKey, editTarget, referenceImageUrls, adId, brandId, pollEditJob, maskingEnabled, annotations, exportMaskDataUrl, userRefs, userRefsAllowed, quickActionsAllowed, quickActionControlsAllowed, goalPickerAllowed, simpleSubmit, onClose]);

  // On open, re-attach to an edit that was started for this image and left in
  // flight (modal closed mid-edit). Resumes polling the same job — the result
  // appears when ready with no re-submit (and therefore no duplicate billing).
  useEffect(() => {
    if (!open) return;
    // Layout-only mode never runs /api/edit-image jobs — don't re-attach to a
    // pending job that some OTHER surface started on the same source image.
    if (isSimpleMode) return;
    if (submittingRef.current || pollingRef.current || preparingNextEditRef.current) return;
    const pending = findPendingImageEdit(imageUrl, editTarget);
    if (pending) {
      activeJobRef.current = pending.jobId;
      setLayeredQuality(pending.layeredQuality);
      if (pending.resultImageUrl) {
        if (isImageEditSaving(pending.jobId)) {
          setIsSubmitting(true);
          setEditPhase("saving");
          return;
        }
        // Reopening transfers a pending/background job to explicit review.
        if (pending.delivery !== "review") updatePendingImageEdit(pending.jobId, { delivery: "review" });
        setIsSubmitting(false);
        setResultImageUrl(pending.resultImageUrl);
        setError(pending.error ?? null);
        return;
      }
      if (pending.error) {
        setIsSubmitting(false);
        setError(pending.error);
        return;
      }
      if (pending.sourceImageUrl !== imageUrl) {
        setContinuedSourceUrl(pending.sourceImageUrl);
      }
      void pollEditJob(pending.jobId);
    } else {
      setIsSubmitting(isImageEditSubmitting(ownerKey));
    }
    // Only re-check when the modal (re)opens for a given source image.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, imageUrl, ownerKey, trackerRevision]);

  const handleAccept = useCallback(async () => {
    if (!resultImageUrl || isAccepting) return;
    setIsAccepting(true);
    setError(null);
    const acceptanceToken = pollTokenRef.current;
    try {
      const jobId = activeJobRef.current;
      if (jobId) await persistPendingImageEdit(jobId, async () => { await onAccept(resultImageUrl); });
      else await onAccept(resultImageUrl);
      if (jobId && layeredQuality) void fetch("/api/edit-image/layers/outcome", {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId, outcome: "accept" }),
      }).catch(() => {});
      if (pollTokenRef.current === acceptanceToken) onClose();
    } catch (e: any) {
      if (activeJobRef.current) updatePendingImageEdit(activeJobRef.current, { error: e?.message || "Failed to save the edit", delivery: "review" });
      if (pollTokenRef.current === acceptanceToken) setError(e?.message || "Failed to save the edit");
    } finally {
      if (pollTokenRef.current === acceptanceToken) setIsAccepting(false);
    }
  }, [resultImageUrl, onAccept, onClose, isAccepting, layeredQuality]);

  const handleTryAgain = useCallback(() => {
    preparingNextEditRef.current = false;
    if (activeJobRef.current && layeredQuality) void fetch("/api/edit-image/layers/outcome", {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ jobId: activeJobRef.current, outcome: "discard" }),
    }).catch(() => {});
    if (activeJobRef.current) clearPendingImageEdit(activeJobRef.current);
    activeJobRef.current = null;
    setLayeredQuality(undefined);
    setResultImageUrl(null);
    setContinuedSourceUrl(null);
    setError(null);
  }, [layeredQuality]);

  const handleContinueEditingResult = useCallback(() => {
    if (!resultImageUrl) return;
    preparingNextEditRef.current = true;
    setContinuedSourceUrl(resultImageUrl);
    setResultImageUrl(null);
    setPrompt("");
    setError(null);
  }, [resultImageUrl]);

  if (open && layeredMode && layeredAllowed && brandId && adId && !resultImageUrl) {
    const scope = layeredScope;
    return <Dialog open={open} onOpenChange={value => { if (!value) onClose(); }}>
      <DialogContent className="max-w-[1150px] w-[96vw] max-h-[94vh] overflow-y-auto p-4">
        <DialogHeader>
          <DialogTitle>Layered edit</DialogTitle>
          <DialogDescription className="sr-only">Edit image layers.</DialogDescription>
        </DialogHeader>
          <LayeredEditEntry key={`${scope}:${sourceForEdit}`} brandId={brandId} adId={adId} imageUrl={sourceForEdit}
            onBack={() => setLayeredMode(false)}
            onPreview={(result: LayeredRenderResult) => {
              if (layeredScopeRef.current !== scope) return;
              rememberPendingImageEdit(imageUrl, result.jobId, sourceForEdit, editTarget);
              const quality = { decompositionId: result.decompositionId, baselineDiff: result.baselineDiff,
                lowFidelity: result.lowFidelity, fontFallback: result.fontFallback };
              updatePendingImageEdit(result.jobId, { delivery: "review", resultImageUrl: result.image_url, layeredQuality: quality });
              activeJobRef.current = result.jobId;
              setLayeredQuality(quality); setResultImageUrl(result.image_url); setError(null); setLayeredMode(false);
            }} />
      </DialogContent>
    </Dialog>;
  }
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !isAccepting) onClose(); }}>
      <DialogContent className="max-w-[780px] w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="edit-image-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Paintbrush className="w-4 h-4" />
            {title ?? "Edit Image"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {description ?? "Describe what you want to change and the AI will regenerate the image with your edits."}
          </DialogDescription>
          {adId && (
            <p className="text-xs text-muted-foreground">
              For supported ad copy edits, text is fit locally; the replacement font may approximate the source.
            </p>
          )}
        </DialogHeader>

        <div className="space-y-3">
          {resultImageUrl && layeredQuality && (
            <div className="text-xs rounded-md border p-3 space-y-1" data-testid="layered-preview-quality">
              {layeredQuality.lowFidelity && <p role="alert">{LAYERED_EDIT_WARNING}</p>}
              <p>Approximate local edit. Seams and overlapping layers may differ even with a low baseline score.</p>
              {layeredQuality.fontFallback && <p>Some original fonts were unavailable. A fallback font was used.</p>}
              <p>Reconstruction baseline difference: {layeredQuality.baselineDiff.toFixed(4)}</p>
              <Button size="sm" variant="outline" disabled={isAccepting} onClick={() => {
                handleTryAgain(); setLayeredMode(true);
              }}>Back to layers</Button>
            </div>
          )}
          <div className="flex flex-col items-center gap-3">
            <div className="relative rounded-lg overflow-hidden border border-border bg-muted/30 w-full max-w-[700px]">
              <img
                ref={imgRef}
                src={displayedSrc}
                alt="Image to edit"
                className="w-full h-auto object-contain"
                draggable={false}
                onLoad={syncCanvasToImage}
                data-testid="edit-image-preview"
              />
              {/* Mask painting overlay — natural-resolution canvas scaled to the
                  displayed image. Strokes are drawn opaque white and shown at
                  ~45% opacity; kept mounted (hidden when a result is shown) so
                  strokes survive toggling. */}
              {!resultImageUrl && (
                <canvas
                  ref={maskCanvasRef}
                  className={`absolute inset-0 w-full h-full opacity-45 ${
                    maskingEnabled && !isSubmitting
                      ? `${maskTool === "click" ? "cursor-pointer" : "cursor-crosshair"} touch-none`
                      : "pointer-events-none"
                  }`}
                  onPointerDown={handlePointerDown}
                  onPointerMove={handlePointerMove}
                  onPointerUp={handlePointerUp}
                  onPointerCancel={handlePointerUp}
                  data-testid="edit-image-mask-canvas"
                />
              )}
              {/* Numbered markers — one per selection, matching the comment
                  rows below. A pending click shows a spinner while SAM 2
                  resolves; the canvas stays interactive throughout. */}
              {!resultImageUrl && maskingEnabled && naturalDims &&
                annotations.map((a, idx) => (
                  <div
                    key={a.id}
                    className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                    style={{
                      left: `${(a.markerX / naturalDims.w) * 100}%`,
                      top: `${(a.markerY / naturalDims.h) * 100}%`,
                    }}
                    data-testid={`marker-annotation-${idx}`}
                  >
                    <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground border-2 border-white shadow-md flex items-center justify-center text-[11px] font-bold">
                      {a.status === "pending" ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        idx + 1
                      )}
                    </div>
                  </div>
                ))}
              {(isSubmitting || isAccepting) && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-white" />
                    <span className="text-sm text-white font-medium" data-testid="edit-image-phase">
                      {isAccepting ? "Saving..." : imageEditPhaseLabel(editPhase)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {!resultImageUrl && (
              <div className="w-full space-y-2">
                {layeredAllowed && <Button type="button" size="sm" variant="outline" disabled={isSubmitting || isAccepting}
                  data-testid="button-layered-edit" onClick={() => setLayeredMode(true)}>Layered edit</Button>}
                {/* Task #1972 — goal picker (grant-gated). Selecting a goal
                    SUGGESTS a default tool + shows a short inline hint; the
                    brush/click toggle below stays fully usable in every goal
                    and switching tools never resets the goal. Skipping the
                    picker (or "Describe it") = today's freeform flow. */}
                {goalPickerAllowed && maskingEnabled && (
                  <div className="w-full space-y-1" data-testid="section-edit-goal-picker">
                    <p className="text-xs font-medium text-foreground">What do you want to do?</p>
                    <div className="flex flex-wrap items-center gap-1">
                      {(
                        [
                          { goal: "remove" as const, label: "Remove something" },
                          { goal: "edit_text" as const, label: "Edit text" },
                          { goal: "style" as const, label: "Change color or style" },
                          { goal: "describe" as const, label: "Describe it" },
                        ]
                      ).map(({ goal, label }) => (
                        <button
                          key={goal}
                          type="button"
                          onClick={() => selectGoal(goal)}
                          disabled={isSubmitting}
                          className={`h-7 px-2.5 rounded-md border text-[11px] font-medium transition-colors ${
                            editGoal === goal
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-input text-muted-foreground hover:text-foreground"
                          }`}
                          aria-pressed={editGoal === goal}
                          data-testid={`edit-goal-${goal}`}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    {/* Task #2040 follow-up — no hint for "remove": both
                        tools work and the toggle is right there, so the
                        extra line was judged unnecessary. */}
                    {editGoal === "edit_text" && (
                      <p className="text-[11px] text-muted-foreground" data-testid="hint-goal-edit-text">
                        Paint over the text with the brush. For stylized text that's more graphic than type, Click select works too.
                      </p>
                    )}
                    {editGoal === "style" && (
                      <p className="text-[11px] text-muted-foreground" data-testid="hint-goal-style">
                        Brush and Click select both work well here — paint the area or tap the object, then describe the new color or style.
                      </p>
                    )}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                  {regionEditingAllowed && (
                    <>
                      <Button
                        type="button"
                        variant={maskingEnabled ? "default" : "outline"}
                        size="sm"
                        className="h-8"
                        onClick={() => setMaskingEnabled((v) => !v)}
                        disabled={isSubmitting}
                        data-testid="button-toggle-mask"
                      >
                        <Lasso className="w-3.5 h-3.5 mr-1" />
                        {maskingEnabled ? "Selecting area" : "Select area"}
                      </Button>
                      {/* In-modal help: gated identically to the region tools —
                          users without the beta grant never see it. Purely
                          informational; no edit behavior changes. */}
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-muted-foreground"
                            aria-label="How the edit modes work"
                            data-testid="button-edit-modes-help"
                          >
                            <HelpCircle className="w-4 h-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          align="start"
                          className="w-[380px] max-w-[92vw] max-h-[60vh] overflow-y-auto p-4 text-xs"
                          data-testid="popover-edit-modes-help"
                        >
                          <div className="space-y-3.5">
                            <p className="text-sm font-semibold">Three ways to edit</p>

                            <div className="space-y-1" data-testid="help-section-global">
                              <p className="flex items-center gap-1.5 font-medium">
                                <Wand2 className="w-3.5 h-3.5 text-primary" />
                                Global — describe a change to the whole image
                              </p>
                              <p className="text-muted-foreground">
                                Turn "Select area" off and type what should change. Best for broad
                                changes like style, lighting, or background swaps. Describe the overall
                                change, not a spot fix.
                              </p>
                              <ul className="list-disc pl-4 text-muted-foreground space-y-0.5">
                                <li>"Make the lighting warmer and more golden"</li>
                                <li>"Change the background to a beach at sunset"</li>
                                <li>"Give the whole image a soft film-photo look"</li>
                              </ul>
                            </div>

                            <div className="space-y-1" data-testid="help-section-click">
                              <p className="flex items-center gap-1.5 font-medium">
                                <MousePointerClick className="w-3.5 h-3.5 text-primary" />
                                Click select — pick a whole object with one click
                              </p>
                              <p className="text-muted-foreground">
                                Turn on "Select area", choose Click select, and click a distinct object
                                (a logo, a person, a product). It's selected automatically and gets its
                                own comment box.
                              </p>
                              <ul className="list-disc pl-4 text-muted-foreground space-y-0.5">
                                <li>"Remove this"</li>
                                <li>"Make this shirt navy blue"</li>
                                <li>"Replace this text with 'Summer Sale'"</li>
                              </ul>
                            </div>

                            <div className="space-y-1" data-testid="help-section-brush">
                              <p className="flex items-center gap-1.5 font-medium">
                                <Paintbrush className="w-3.5 h-3.5 text-primary" />
                                Brush — paint or circle a custom area
                              </p>
                              <p className="text-muted-foreground">
                                For irregular areas that aren't a single object: paint over the region,
                                or circle it and the inside fills in. Each stroke becomes its own
                                selection with its own comment.
                              </p>
                              <ul className="list-disc pl-4 text-muted-foreground space-y-0.5">
                                <li>"Clean up this cluttered corner"</li>
                                <li>"Make this whole area darker"</li>
                                <li>"Remove everything painted here"</li>
                              </ul>
                            </div>

                            <div className="space-y-1 rounded-md bg-muted/50 p-2.5" data-testid="help-section-tips">
                              <p className="flex items-center gap-1.5 font-medium">
                                <Lightbulb className="w-3.5 h-3.5 text-primary" />
                                Prompting tips
                              </p>
                              <ul className="list-disc pl-4 text-muted-foreground space-y-0.5">
                                <li>Be specific about color, material, and exact text (quote the text you want).</li>
                                <li>One change per selection — add another selection for another change.</li>
                                <li>Each comment applies only to its own selection; keep it short and direct.</li>
                                <li>To remove something, just say "remove this" — only describe a replacement if you want something specific.</li>
                              </ul>
                            </div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    </>
                  )}
                  {maskingEnabled && (
                    <>
                      <div className="flex items-center rounded-md border border-input overflow-hidden">
                        <Button
                          type="button"
                          variant={maskTool === "brush" ? "secondary" : "ghost"}
                          size="sm"
                          className="h-8 rounded-none"
                          onClick={() => setMaskTool("brush")}
                          disabled={isSubmitting}
                          data-testid="button-mask-tool-brush"
                        >
                          <Paintbrush className="w-3.5 h-3.5 mr-1" />
                          Brush
                        </Button>
                        <Button
                          type="button"
                          variant={maskTool === "click" ? "secondary" : "ghost"}
                          size="sm"
                          className="h-8 rounded-none"
                          onClick={() => setMaskTool("click")}
                          disabled={isSubmitting}
                          data-testid="button-mask-tool-click"
                        >
                          <MousePointerClick className="w-3.5 h-3.5 mr-1" />
                          Click select
                        </Button>
                      </div>
                      {maskTool === "brush" && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground whitespace-nowrap">Size</span>
                          <Slider
                            value={[brushSize]}
                            onValueChange={(v) => setBrushSize(v[0] ?? 40)}
                            min={8}
                            max={96}
                            step={1}
                            className="w-28"
                            disabled={isSubmitting}
                            data-testid="slider-brush-size"
                          />
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8"
                        onClick={undoLastAction}
                        disabled={isSubmitting || annotations.length === 0}
                        data-testid="button-undo-mask"
                      >
                        <Undo2 className="w-3.5 h-3.5 mr-1" />
                        Undo
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-8"
                        onClick={clearMask}
                        disabled={isSubmitting || annotations.length === 0}
                        data-testid="button-clear-mask"
                      >
                        <Eraser className="w-3.5 h-3.5 mr-1" />
                        Clear
                      </Button>
                    </>
                  )}
                </div>
                {maskingEnabled && (
                  <p className="text-[11px] text-muted-foreground" data-testid="text-mask-hint">
                    {maskTool === "click"
                      ? "Click an object to select it automatically — each click becomes a numbered selection with its own comment below. Mix in the brush any time. The edits stay inside the selected regions (approximately), and the rest of the image is preserved."
                      : "Paint over an area you want to change, or circle it — each stroke becomes a numbered selection with its own comment below. The edits stay inside the selected regions (approximately), and the rest of the image is preserved."}
                    {referenceImageUrls && referenceImageUrls.length > 0
                      ? " Product reference photos aren't used for area edits; the unselected product stays exactly as-is."
                      : ""}
                  </p>
                )}
                {/* Task #1432 — user-uploaded reference images (beta:
                    edit_reference_images). Uploaded to durable storage on
                    select; sent on submit in their own field. */}
                {userRefsAllowed && (
                  <div className="w-full space-y-1.5" data-testid="section-user-references">
                    {maskingEnabled && userRefs.length > 0 && (
                      <div
                        className="flex items-start gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] leading-snug text-amber-700 dark:text-amber-400"
                        data-testid="notice-user-references-region-limit"
                      >
                        <Info className="w-3.5 h-3.5 shrink-0 mt-[1px]" />
                        <span>
                          With area selections, reference images only apply to selections that <span className="font-medium">change or replace</span> something. Selections that only <span className="font-medium">remove</span> something can't use references. Use the thumbnails under each selection's comment to choose which references apply to it (all are on by default).
                        </span>
                      </div>
                    )}
                    {userRefs.length > 0 && (
                      <div className="space-y-1.5">
                        {userRefs.map((r, idx) => (
                          <div
                            key={r.id}
                            className="flex items-start gap-2 rounded-md border border-border bg-muted/20 p-1.5"
                            data-testid={`card-user-reference-${idx}`}
                          >
                            <div
                              className="relative w-14 h-14 shrink-0 rounded-md border border-border overflow-hidden bg-muted/30"
                              data-testid={`thumb-user-reference-${idx}`}
                            >
                              <img src={r.previewUrl} alt={`Reference ${idx + 1}`} className="w-full h-full object-cover" draggable={false} />
                              {r.status === "uploading" && (
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                  <Loader2 className="w-4 h-4 animate-spin text-white" />
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={() => removeUserRef(r.id)}
                                disabled={isSubmitting}
                                className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80"
                                aria-label={`Remove reference ${idx + 1}`}
                                data-testid={`button-remove-user-reference-${idx}`}
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <div className="flex-1 min-w-0 space-y-1">
                              <Select
                                value={r.purpose}
                                onValueChange={(v) => updateUserRefPurpose(r.id, v as UserRefPurpose)}
                                disabled={isSubmitting}
                              >
                                <SelectTrigger
                                  className="h-7 text-xs"
                                  data-testid={`select-user-reference-purpose-${idx}`}
                                >
                                  <SelectValue placeholder="Use as…" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="auto" data-testid={`option-user-reference-purpose-auto-${idx}`}>
                                    Auto — let us decide
                                  </SelectItem>
                                  <SelectItem value="environment" data-testid={`option-user-reference-purpose-environment-${idx}`}>
                                    Environment / setting
                                  </SelectItem>
                                  <SelectItem value="product" data-testid={`option-user-reference-purpose-product-${idx}`}>
                                    Product to feature
                                  </SelectItem>
                                  <SelectItem value="product_environment" data-testid={`option-user-reference-purpose-product-environment-${idx}`}>
                                    Product + environment
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-[10px] text-muted-foreground/80 leading-snug" data-testid={`text-user-reference-purpose-hint-${idx}`}>
                                {r.purpose === "product"
                                  ? "This product will appear in the edited image."
                                  : r.purpose === "environment"
                                    ? "Steers background, lighting & mood only."
                                    : r.purpose === "product_environment"
                                      ? "This product appears in the edit AND its setting becomes the scene."
                                      : "We'll detect: product shot → featured product; scene → environment."}
                              </p>
                              <Input
                                value={r.note}
                                onChange={(e) => updateUserRefNote(r.id, e.target.value)}
                                placeholder="Optional note (e.g. warm sunset tones)"
                                disabled={isSubmitting}
                                className="h-7 text-xs"
                                data-testid={`input-user-reference-note-${idx}`}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {userRefs.length < MAX_USER_REFS && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-8"
                          onClick={() => refFileInputRef.current?.click()}
                          disabled={isSubmitting}
                          data-testid="button-add-user-reference"
                        >
                          <ImagePlus className="w-3.5 h-3.5 mr-1" />
                          Add reference image
                        </Button>
                      )}
                      <input
                        ref={refFileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          void handleRefFileSelected(e.target.files?.[0]);
                          e.target.value = "";
                        }}
                        data-testid="input-user-reference-file"
                      />
                    </div>
                    {userRefs.length > 0 && (
                      <p className="text-[11px] text-muted-foreground" data-testid="text-user-reference-hint">
                        Each reference can steer the look &amp; feel of the edit or add a product that should appear — pick a purpose per image, or leave it on Auto (up to {MAX_USER_REFS}, 10 MB each).
                      </p>
                    )}
                  </div>
                )}
                {/* Per-selection comment rows — one per click/stroke, numbered
                    to match the markers on the image. All comments are applied
                    together on one Submit. */}
                {usingRegions && (
                  <div className="w-full space-y-1.5">
                    {annotations.map((a, idx) => (
                      <div
                        key={a.id}
                        className="flex items-center gap-2"
                        data-testid={`annotation-row-${idx}`}
                      >
                        <div className="w-5 h-5 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-[10px] font-bold">
                          {idx + 1}
                        </div>
                        {a.kind === "click" ? (
                          <MousePointerClick className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                        ) : (
                          <Paintbrush className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
                        )}
                        {a.status === "pending" ? (
                          <div className="flex-1 flex items-center gap-2 h-8 text-xs text-muted-foreground">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Selecting object...
                          </div>
                        ) : (
                          <div className="flex-1 min-w-0 space-y-1">
                            {/* Task #1971 — quick actions (grant-gated): explicit
                                Remove / Edit text per selection, skipping server-side
                                intent guessing. Clicking the active one toggles back
                                to the freeform comment. */}
                            {quickActionControlsAllowed && (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => setAnnotationQuickAction(a.id, "removal")}
                                  disabled={isSubmitting}
                                  className={`h-6 px-2 rounded-md border text-[11px] font-medium transition-colors ${
                                    a.quickAction === "removal"
                                      ? "border-primary bg-primary/10 text-primary"
                                      : "border-input text-muted-foreground hover:text-foreground"
                                  }`}
                                  aria-pressed={a.quickAction === "removal"}
                                  data-testid={`quick-action-remove-${idx}`}
                                >
                                  Remove
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setAnnotationQuickAction(a.id, "text_edit")}
                                  disabled={isSubmitting}
                                  className={`h-6 px-2 rounded-md border text-[11px] font-medium transition-colors ${
                                    a.quickAction === "text_edit"
                                      ? "border-primary bg-primary/10 text-primary"
                                      : "border-input text-muted-foreground hover:text-foreground"
                                  }`}
                                  aria-pressed={a.quickAction === "text_edit"}
                                  data-testid={`quick-action-edit-text-${idx}`}
                                >
                                  Edit text
                                </button>
                              </div>
                            )}
                            {a.quickAction === "text_edit" ? (
                              <>
                                <input
                                  type="text"
                                  value={a.currentText}
                                  onChange={(e) => setAnnotationCurrentText(a.id, e.target.value)}
                                  placeholder="Current text (optional)"
                                  className="w-full h-8 rounded-md border border-input bg-background px-2.5 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                  disabled={isSubmitting}
                                  maxLength={500}
                                  data-testid={`input-annotation-current-text-${idx}`}
                                />
                                <input
                                  type="text"
                                  value={a.newText}
                                  onChange={(e) => setAnnotationNewText(a.id, e.target.value)}
                                  placeholder="New text (required)"
                                  className="w-full h-8 rounded-md border border-input bg-background px-2.5 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                  disabled={isSubmitting}
                                  maxLength={500}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !isSubmitting && canSubmit) handleSubmit();
                                  }}
                                  data-testid={`input-annotation-new-text-${idx}`}
                                />
                              </>
                            ) : (
                              <input
                                type="text"
                                value={a.comment}
                                onChange={(e) => setAnnotationComment(a.id, e.target.value)}
                                placeholder={
                                  a.quickAction === "removal"
                                    ? "Optional note (nothing required — the selection is erased)"
                                    : a.quickAction === "attribute_edit"
                                      ? "Describe the new color or style (e.g., 'make it navy blue', 'matte finish')"
                                      : `What should change here? (e.g., 'remove this', 'make it blue')`
                                }
                                className="w-full h-8 rounded-md border border-input bg-background px-2.5 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                disabled={isSubmitting}
                                maxLength={2000}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !isSubmitting && canSubmit) handleSubmit();
                                }}
                                data-testid={`input-annotation-comment-${idx}`}
                              />
                            )}
                            {/* Per-selection reference picker: with references
                                uploaded, each selection can toggle which of them
                                apply to it (all on by default). Removal-style
                                comments never use references server-side, so the
                                toggles are simply irrelevant there. */}
                            {userRefsAllowed && userRefs.length > 0 && (
                              <div className="flex flex-wrap items-center gap-1" data-testid={`refs-annotation-${idx}`}>
                                <span className="text-[10px] text-muted-foreground mr-0.5">Refs:</span>
                                {userRefs.map((r, rIdx) => {
                                  const active = a.refIds === null || a.refIds.includes(r.id);
                                  return (
                                    <button
                                      type="button"
                                      key={r.id}
                                      onClick={() => toggleAnnotationRef(a.id, r.id)}
                                      disabled={isSubmitting}
                                      className={`relative h-7 w-7 shrink-0 rounded border overflow-hidden transition-opacity ${
                                        active
                                          ? "border-primary ring-1 ring-primary"
                                          : "border-input opacity-40 hover:opacity-70"
                                      }`}
                                      title={
                                        active
                                          ? `Reference ${rIdx + 1} is used for this selection — click to exclude it`
                                          : `Reference ${rIdx + 1} is excluded from this selection — click to include it`
                                      }
                                      aria-pressed={active}
                                      data-testid={`toggle-annotation-ref-${idx}-${rIdx}`}
                                    >
                                      <img src={r.previewUrl} alt="" className="h-full w-full object-cover" />
                                      {active && (
                                        <span className="absolute bottom-0 right-0 rounded-tl bg-primary text-primary-foreground p-px">
                                          <Check className="w-2.5 h-2.5" />
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 shrink-0"
                          onClick={() => removeAnnotation(a.id)}
                          disabled={isSubmitting}
                          aria-label={`Remove selection ${idx + 1}`}
                          data-testid={`button-remove-annotation-${idx}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {/* Task #1591 — title the whole-image input so its scope is
                    obvious once region tools exist. Gated on the region beta
                    so no-grant users keep the untouched legacy layout. */}
                {!usingRegions && regionEditingAllowed && (
                  <p className="text-xs font-medium text-foreground" data-testid="label-overall-ad-updates">
                    Overall ad updates
                  </p>
                )}
                <div className="w-full flex gap-2">
                {!usingRegions && (
                  <input
                    type="text"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={
                      readyUserRefCount > 0
                        ? "Optional — leave blank to apply your reference image(s) as selected above"
                        : regionEditingAllowed
                          ? "Describe a change to the whole image (e.g., 'make the lighting warmer', 'change the background to a beach at sunset')"
                          : "Describe your changes (e.g., 'make the castle pink', 'change the text to say Hello')"
                    }
                    className="flex-1 h-9 rounded-md border border-input bg-background px-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    disabled={isSubmitting}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isSubmitting) handleSubmit();
                    }}
                    data-testid="edit-image-prompt-input"
                  />
                )}
                <Button
                  size="sm"
                  className={usingRegions ? "h-9 px-4 ml-auto" : "h-9 px-4"}
                  onClick={handleSubmit}
                  disabled={isSubmitting || !canSubmit || anyRefUploading}
                  data-testid="edit-image-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />
                      {imageEditPhaseLabel(editPhase)}
                    </>
                  ) : error && activeJobRef.current ? (
                    "Start a new edit (new render)"
                  ) : usingRegions && readyAnnotations.length > 1 ? (
                    `Submit ${readyAnnotations.length} edits`
                  ) : (
                    "Submit"
                  )}
                </Button>
                </div>
                {/* Task #2046 follow-up — once a real edit job is in flight it
                    survives the modal: reassure the user they can leave.
                    Simple mode (meme layout edits) has no background job. */}
                {isSubmitting && !isSimpleMode && (
                  <p
                    className="text-xs text-muted-foreground mt-2"
                    data-testid="edit-image-safe-to-close-note"
                  >
                    You can close this window — the edit keeps running in the background
                    and will be here when you come back.
                  </p>
                )}
                {isSubmitting && editPhase === "checking" && (
                  <div className="mt-2 text-xs text-muted-foreground">
                    The outcome is not confirmed yet. We are checking the original edit, not starting a new render.
                    <Button variant="outline" size="sm" className="ml-2"
                      data-testid="edit-image-check-status"
                      onClick={() => { if (activeJobRef.current) checkImageEditNow(activeJobRef.current); }}>
                      Check existing edit
                    </Button>
                  </div>
                )}
              </div>
            )}

            {resultImageUrl && (
              <div className="w-full flex items-center justify-between gap-2">
                <span className="text-sm text-emerald-600 dark:text-emerald-400 font-medium">
                  {error ? "Edit complete — awaiting save retry" : "Edit ready for review"}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={handleContinueEditingResult}
                    disabled={isAccepting}
                    data-testid="edit-image-continue-result"
                  >
                    Edit this result
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    onClick={handleTryAgain}
                    disabled={isAccepting}
                    data-testid="edit-image-try-again"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" />
                    Start a new edit
                  </Button>
                  <Button
                    size="sm"
                    className="h-8"
                    onClick={handleAccept}
                    disabled={isAccepting}
                    data-testid="edit-image-accept"
                  >
                    {isAccepting ? (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-3 h-3 mr-1" />
                        Accept
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-500 w-full" data-testid="edit-image-error">{error}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
